import { useMemo } from 'react'

import { LYNK_WORKFLOW_DATA } from 'src/features/lynk/schemas/workflows'
import { cn } from 'src/shared/utils/cn'

import type { WorkflowPreview } from '../api/use-first-workflow-preview'
import { WorkflowPreviewStatus } from '../api/use-first-workflow-preview'

type BCWorkflowCardProps = {
  workflow: WorkflowPreview
  isFirst: boolean
  isLast: boolean
  connectorLength: number
}

const BCWorkflowCard = ({ workflow, isFirst, isLast, connectorLength }: BCWorkflowCardProps) => {
  const workflowData = LYNK_WORKFLOW_DATA[workflow.name]

  const variantStyles = useMemo(() => {
    switch (workflow.status) {
      case WorkflowPreviewStatus.COMPLETE:
        return {
          cardClassName: 'border-primary bg-primary',
          titleClassName: 'text-white',
          subtitleClassName: 'text-white/75',
          iconContainerClassName: 'bg-white',
          iconColor: '#1B3029',
        }
      case WorkflowPreviewStatus.MISSING_DATA:
        return {
          cardClassName: 'border-transparent bg-accent',
          titleClassName: 'text-primary',
          subtitleClassName: 'text-muted',
          iconContainerClassName: 'bg-white',
          iconColor: '#1B3029',
        }
      case WorkflowPreviewStatus.INCOMPLETE:
      default:
        return {
          cardClassName: 'border-primary bg-white shadow-[0_0_30px_rgba(27,48,41,0.15)]',
          titleClassName: 'text-primary',
          subtitleClassName: 'text-muted',
          iconContainerClassName: 'bg-primary',
          iconColor: '#FFFFFF',
        }
    }
  }, [workflow.status])

  if (!workflowData) return null

  const { title, icon, category } = workflowData

  return (
    <div className="flex flex-col items-center gap-3 pb-6">
      <div
        className={cn(
          'relative flex h-[170px] w-[210px] flex-col items-center justify-center rounded-[30px] border',
          variantStyles.cardClassName,
        )}
      >
        {!isFirst && connectorLength > 0 ? (
          <div
            className="absolute top-1/2 h-[2px] -translate-y-1/2 bg-primary/30"
            style={{ left: -connectorLength, width: connectorLength }}
          />
        ) : null}

        {!isLast && connectorLength > 0 ? (
          <div
            className="absolute top-1/2 h-[2px] -translate-y-1/2 bg-primary/30"
            style={{ right: -connectorLength, width: connectorLength }}
          />
        ) : null}

        <div
          className={cn(
            'mb-2 flex size-[55px] items-center justify-center rounded-full',
            variantStyles.iconContainerClassName,
          )}
        >
          {icon?.({ size: 24, color: variantStyles.iconColor })}
        </div>
        <p className={cn('text-[13px]', variantStyles.subtitleClassName)}>{category}</p>
        <p className={cn('text-[18px] font-medium tracking-[-0.18px]', variantStyles.titleClassName)}>{title}</p>
      </div>

      {!workflow.workflowEnabled ? (
        <p className="max-w-[560px] text-center text-xs leading-[1.4] text-muted">
          This workflow is off in your Lynk preferences, turning it on here won&apos;t change those settings.
        </p>
      ) : null}
    </div>
  )
}

export default BCWorkflowCard
